import { num, round2 } from '../../utils/invoiceLineMath';
import type { Candidates, GroupStatus, PaymentRun, UpdateRunBody } from '../../lib/paymentRunsApi';

/**
 * The state of the run editor, as plain data (D49 phase 4). The page holds it, this file turns it
 * into what the server is sent, and both directions are pure so they can be tested without a
 * page. The server never believes what the page says about a bill (its supplier, its currency,
 * what it owes): only the bill's id and the amount to pay are sent, and every check is made again
 * there. The checks here only spare a person a round trip.
 *
 * A supplier is a SECTION, one per supplier and currency. It lists the bills saved in the run
 * (ticked) and the other open bills due by the run's date (unticked), so any of them can be
 * added. A supplier that is already recorded is history and is not in the editor at all.
 */

export interface EditRow {
  billId: string;
  invoiceNumber: string;
  /** `yyyy-mm-dd`, or null when the bill has no due date. */
  dueDate: string | null;
  /** What the bill owes NOW; null when the bill no longer exists. */
  outstanding: number | null;
  status: string | null;
  held: boolean;
  /** The other open runs this bill is in. */
  inRuns: string[];
  /** The bill owes less now than this run pays against it. */
  changed: boolean;
  ticked: boolean;
  /** What is typed in the amount box. */
  amount: string;
}

export interface EditSection {
  /** The supplier and the currency; what a reference edit names. */
  key: string;
  supplierName: string;
  currency: string;
  /** The saved group's id, or null for a supplier picked but not saved yet. */
  groupId: string | null;
  groupStatus: GroupStatus | null;
  /** Why the last attempt to record this supplier failed (a code), or ''. */
  errorCode: string;
  /** The reference as saved, or null when the supplier has not been saved yet. */
  savedReference: string | null;
  /** What is typed in the reference box. */
  reference: string;
  rows: EditRow[];
}

export interface EditHeader {
  dueBy: string;
  paymentDate: string;
  method: string;
  note: string;
}

export type RowProblem = 'AMOUNT_REQUIRED' | 'OVER_OWED' | 'BILL_GONE';

export const ROW_PROBLEM_TEXT: Record<RowProblem, string> = {
  AMOUNT_REQUIRED: 'Enter an amount greater than zero.',
  OVER_OWED: 'More than this bill owes.',
  BILL_GONE: 'This bill no longer exists. Untick it.',
};

const byDueThenNumber = (a: EditRow, b: EditRow): number => {
  if (a.dueDate !== b.dueDate) {
    if (!a.dueDate) return 1;
    if (!b.dueDate) return -1;
    return a.dueDate < b.dueDate ? -1 : 1;
  }
  return a.invoiceNumber.localeCompare(b.invoiceNumber, 'en', { numeric: true });
};

const amountText = (value: number): string => value.toFixed(2);

/**
 * The editor's sections from the saved run and the bills due by its date. Bills already paid by a
 * recorded supplier of this run are left out (the server refuses them), and a saved bill is
 * always listed even when it is no longer in the candidates (its date was moved).
 */
export function buildSections(run: PaymentRun, candidates: Candidates | null): EditSection[] {
  const recordedBills = new Set<string>();
  for (const g of run.groups) {
    if (g.status === 'recorded') for (const l of g.lines) recordedBills.add(l.accountsPayableId);
  }

  const sections = new Map<string, EditSection>();
  for (const g of run.groups) {
    if (g.status === 'recorded') continue;
    sections.set(g.key, {
      key: g.key,
      supplierName: g.supplierName,
      currency: g.currency,
      groupId: g.id,
      groupStatus: g.status,
      errorCode: g.errorCode,
      savedReference: g.reference,
      reference: g.reference,
      rows: g.lines.map((l) => ({
        billId: l.accountsPayableId,
        invoiceNumber: l.invoiceNumber,
        dueDate: l.dueDate,
        outstanding: l.current ? l.current.outstanding : l.outstandingAtSave,
        status: l.current ? l.current.status : null,
        held: Boolean(l.current?.held),
        inRuns: [],
        changed: Boolean(l.current?.changed),
        ticked: true,
        amount: amountText(l.amount),
      })),
    });
  }

  for (const s of candidates?.suppliers || []) {
    let section = sections.get(s.key);
    if (!section) {
      section = {
        key: s.key,
        supplierName: s.supplierName,
        currency: s.currency,
        groupId: null,
        groupStatus: null,
        errorCode: '',
        savedReference: null,
        reference: '',
        rows: [],
      };
      sections.set(s.key, section);
    }
    for (const bill of s.bills) {
      const existing = section.rows.find((r) => r.billId === bill.accountsPayableId);
      if (existing) {
        existing.inRuns = bill.inRuns;
        continue;
      }
      if (recordedBills.has(bill.accountsPayableId)) continue;
      section.rows.push({
        billId: bill.accountsPayableId,
        invoiceNumber: bill.invoiceNumber,
        dueDate: bill.dueDate,
        outstanding: bill.outstanding,
        status: bill.status,
        held: bill.held,
        inRuns: bill.inRuns,
        changed: false,
        ticked: false,
        amount: amountText(bill.outstanding),
      });
    }
  }

  const list = Array.from(sections.values()).filter((s) => s.rows.length > 0);
  for (const s of list) s.rows.sort(byDueThenNumber);
  return list.sort(
    (a, b) => a.supplierName.localeCompare(b.supplierName, 'en', { sensitivity: 'base' }) || a.currency.localeCompare(b.currency)
  );
}

/**
 * The editor after the "due by" day (or "show bills with no due date") changed: every TICKED row
 * stays exactly as the person left it (amount, reference and all), and the unticked rows are
 * replaced by what the new candidates offer, so a bill that is no longer due by that day is
 * not left lying about unticked.
 */
export function mergeCandidates(sections: EditSection[], candidates: Candidates | null): EditSection[] {
  const kept: EditSection[] = sections
    .map((s) => ({ ...s, rows: s.rows.filter((r) => r.ticked).map((r) => ({ ...r })) }))
    .filter((s) => s.rows.length > 0);

  const map = new Map(kept.map((s) => [s.key, s]));
  for (const s of candidates?.suppliers || []) {
    let section = map.get(s.key);
    if (!section) {
      const original = sections.find((x) => x.key === s.key);
      section = {
        key: s.key,
        supplierName: s.supplierName,
        currency: s.currency,
        groupId: original?.groupId ?? null,
        groupStatus: original?.groupStatus ?? null,
        errorCode: original?.errorCode ?? '',
        savedReference: original?.savedReference ?? null,
        reference: original?.reference ?? '',
        rows: [],
      };
      map.set(s.key, section);
    }
    for (const bill of s.bills) {
      const existing = section.rows.find((r) => r.billId === bill.accountsPayableId);
      if (existing) {
        existing.inRuns = bill.inRuns;
        continue;
      }
      section.rows.push({
        billId: bill.accountsPayableId,
        invoiceNumber: bill.invoiceNumber,
        dueDate: bill.dueDate,
        outstanding: bill.outstanding,
        status: bill.status,
        held: bill.held,
        inRuns: bill.inRuns,
        changed: false,
        ticked: false,
        amount: amountText(bill.outstanding),
      });
    }
  }

  const list = Array.from(map.values()).filter((s) => s.rows.length > 0);
  for (const s of list) s.rows.sort(byDueThenNumber);
  return list.sort(
    (a, b) => a.supplierName.localeCompare(b.supplierName, 'en', { sensitivity: 'base' }) || a.currency.localeCompare(b.currency)
  );
}

/** What is wrong with a ticked row, or null. An unticked row is never a problem. */
export function rowProblem(row: EditRow): RowProblem | null {
  if (!row.ticked) return null;
  if (row.outstanding === null) return 'BILL_GONE';
  const amount = num(row.amount);
  if (amount === null || !(round2(amount) > 0)) return 'AMOUNT_REQUIRED';
  // Whole cents, the same rule as the server: never more than the bill owes.
  if (Math.round(round2(amount) * 100) > Math.round(row.outstanding * 100)) return 'OVER_OWED';
  return null;
}

export function hasProblems(sections: EditSection[]): boolean {
  return sections.some((s) => s.rows.some((r) => rowProblem(r) !== null));
}

/** What a supplier's ticked rows add up to, in cents (a row with a bad amount counts as nothing). */
export function sectionTotal(section: EditSection): number {
  const cents = section.rows.reduce((sum, r) => {
    if (!r.ticked || rowProblem(r) === 'AMOUNT_REQUIRED') return sum;
    return sum + Math.round((num(r.amount) || 0) * 100);
  }, 0);
  return round2(cents / 100);
}

export function tickedCount(section: EditSection): number {
  return section.rows.filter((r) => r.ticked).length;
}

/** Money by currency for the whole selection, never added across currencies. */
export function selectionTotals(sections: EditSection[]): Array<{ currency: string; amount: number; suppliers: number }> {
  const byCurrency = new Map<string, { cents: number; suppliers: number }>();
  for (const s of sections) {
    if (tickedCount(s) === 0) continue;
    const entry = byCurrency.get(s.currency) || { cents: 0, suppliers: 0 };
    entry.cents += Math.round(sectionTotal(s) * 100);
    entry.suppliers += 1;
    byCurrency.set(s.currency, entry);
  }
  return Array.from(byCurrency.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([currency, e]) => ({ currency, amount: round2(e.cents / 100), suppliers: e.suppliers }));
}

/** The complete list of bills to pay, as the server expects it. */
export function selectionLines(sections: EditSection[]): NonNullable<UpdateRunBody['lines']> {
  const lines: NonNullable<UpdateRunBody['lines']> = [];
  for (const s of sections) {
    for (const r of s.rows) {
      if (r.ticked) lines.push({ accountsPayableId: r.billId, amount: round2(num(r.amount) || 0) });
    }
  }
  return lines;
}

/**
 * References the person changed. Only a supplier that is saved AND still has a ticked bill can
 * carry one (a supplier with nothing ticked is dropped by the server, which would refuse a
 * reference for it). A blank box means "automatic" and is sent as blank.
 */
export function referenceEdits(sections: EditSection[]): NonNullable<UpdateRunBody['groups']> {
  return sections
    .filter((s) => s.savedReference !== null && tickedCount(s) > 0 && s.reference.trim() !== s.savedReference)
    .map((s) => ({ key: s.key, reference: s.reference.trim() }));
}

/**
 * A string that changes whenever anything the person can change has changed, so the page knows
 * there is something unsaved. Recording is offered only when it is equal to what was saved:
 * what is recorded is exactly what was reviewed and stored.
 */
export function editSignature(sections: EditSection[], header: EditHeader): string {
  return JSON.stringify({
    header,
    sections: sections.map((s) => ({
      key: s.key,
      reference: s.reference.trim(),
      rows: s.rows.filter((r) => r.ticked).map((r) => [r.billId, r.amount.trim()]),
    })),
  });
}
